import React, { useState, useRef } from 'react';
import {
    Box,
    IconButton,
    Typography,
    useTheme,
    useMediaQuery,
    Container,
    Modal,
    Backdrop,
    Fade
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Close as CloseIcon
} from '@mui/icons-material';

const ImageCarousel = ({ images }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [currentIndex, setCurrentIndex] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [modalImageIndex, setModalImageIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const containerRef = useRef(null);

    const maxIndex = isMobile ? images.length - 1 : Math.max(0, images.length - 3);

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? maxIndex : prevIndex - 1
        );
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex >= maxIndex ? 0 : prevIndex + 1
        );
    };

    const handleCardClick = (image) => {
        setSelectedImage(image);
        setModalOpen(true);
        // Find the index of the clicked image
        const imageIndex = images.findIndex(img => img.url === image.url);
        setModalImageIndex(imageIndex);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedImage(null);
        setModalImageIndex(0);
    };

    const goToPreviousModal = () => {
        const newIndex = modalImageIndex === 0 ? images.length - 1 : modalImageIndex - 1;
        setModalImageIndex(newIndex);
        setSelectedImage(images[newIndex]);
    };

    const goToNextModal = () => {
        const newIndex = modalImageIndex === images.length - 1 ? 0 : modalImageIndex + 1;
        setModalImageIndex(newIndex);
        setSelectedImage(images[newIndex]);
    };

    // Touch handlers for mobile swipe
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        // e.preventDefault();
        // e.stopPropagation();
        

        setTouchEnd(e.targetTouches[0].clientX);

    // Prevent vertical scrolling while swiping horizontally
    if (touchStart && e.targetTouches[0].clientY) {
        const touchY = e.targetTouches[0].clientY;
        const deltaX = Math.abs(e.targetTouches[0].clientX - touchStart);
        const deltaY = Math.abs(touchY);

        // If horizontal movement is greater than vertical, prevent default scrolling
        if (deltaX > deltaY) {
            e.preventDefault();
        }
    }
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            goToNext();
        }
        if (isRightSwipe) {
            goToPrevious();
        }

        setTouchStart(null);
        setTouchEnd(null);
    };

    // Modal touch handlers
    const handleModalTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleModalTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleModalTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            goToNextModal();
        }
        if (isRightSwipe) {
            goToPreviousModal();
        }

        setTouchStart(null);
        setTouchEnd(null);
    };

    return (
        <Box sx={{ py: 8, backgroundColor: theme.palette.background.main }}>
            <Container maxWidth="lg">
                <Box sx={{ position: 'relative' }}>
                    {/* Navigation Arrows - Hidden on mobile */}

                    <IconButton
                        onClick={goToPrevious}
                        sx={{
                            position: 'absolute',
                            left: isMobile ? 10 : -20,
                            top: '45%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            zIndex: 3,
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.9)'
                            }
                        }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>

                    <IconButton
                        onClick={goToNext}
                        sx={{
                            position: 'absolute',
                            right: isMobile ? 10 : -20,
                            top: isMobile ? '45%' : '45%',
                            transform: 'translateY(-50%)',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            zIndex: 3,
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.9)'
                            }
                        }}
                    >
                        <ChevronRightIcon />
                    </IconButton>


                    {/* Images Container */}
                    <Box
                        ref={containerRef}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        sx={{
                            overflow: 'hidden',
                            position: 'relative',
                            touchAction: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: isMobile ? 0 : 3,
                                transform: isMobile
                                    ? `translateX(-${currentIndex * 100}%)`
                                    : `translateX(-${currentIndex * (100 / 3)}%)`,
                                transition: 'transform 0.5s ease-in-out',
                                width: isMobile ? `100%` : `100%`,
                            }}
                        >
                            {images.map((image, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: isMobile ? '0 0 100%' : '0 0 calc(33.333% - 16px)',
                                        minWidth: 0,
                                        px: isMobile ? 0 : 0,
                                        position: 'relative',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        onClick={() => handleCardClick(image)}
                                        sx={{
                                            position: 'relative',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            boxShadow: 3,
                                            height: isMobile ? 250 : 300,
                                            background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${image.url})`,
                                            backgroundSize: isMobile ? 'cover' : 'cover',
                                              backgroundPosition: 'center',
                                            //   backgroundRepeat: 'no-repeat',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-end',
                                            p: 3,
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease-in-out',
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                boxShadow: 6
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Dots Indicator */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1,
                            mt: 4
                        }}
                    >
                        {Array.from({ length: maxIndex + 1 }, (_, index) => (
                            <Box
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: index === currentIndex ? 'primary.dark' : 'rgba(255,255,255,0.3)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: index === currentIndex ? 'primary.main' : 'rgba(255,255,255,0.6)'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Container>

            {/* Modal */}
            <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                    sx: {
                        backdropFilter: 'blur(8px)',
                        backgroundColor: 'rgba(0,0,0,0.8)'
                    }
                }}
            >
                <Fade in={modalOpen}>
                    <Box
                        onTouchStart={handleModalTouchStart}
                        onTouchMove={handleModalTouchMove}
                        onTouchEnd={handleModalTouchEnd}
                        sx={{
                            position: 'absolute',
                            top: {
                                xs: '40%',
                                sm: '40%',
                                md: '50%',
                            },
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90%',
                            maxWidth: 800,
                            maxHeight: '90vh',
                            outline: 'none'
                        }}
                    >
                        {selectedImage && (
                            <Box sx={{ position: 'relative' }}>
                                {/* Close Button */}
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        position: 'absolute',
                                        top: -40,
                                        right: 0,
                                        color: 'white',
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.7)'
                                        },
                                        zIndex: 4
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>

                                {/* Navigation Buttons */}
                                <IconButton
                                    onClick={goToPreviousModal}
                                    sx={{
                                        position: 'absolute',
                                        left: -60,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        zIndex: 4,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.9)'
                                        }
                                    }}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>

                                <IconButton
                                    onClick={goToNextModal}
                                    sx={{
                                        position: 'absolute',
                                        right: -60,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        zIndex: 4,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0,0,0,0.9)'
                                        }
                                    }}
                                >
                                    <ChevronRightIcon />
                                </IconButton>

                                {/* Image */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        maxHeight: '70vh',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: 8
                                    }}
                                >
                                    <img
                                        // src={selectedImage.url.replace('w=400&h=300&fit=crop', 'w=1200&h=800&fit=crop')}
                                        src={selectedImage.url}
                                        alt={selectedImage.title}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            objectPosition: 'center !important'
                                        }}
                                    />
                                </Box>

                                {/* Image Info */}
                                <Box
                                    sx={{
                                        mt: 2,
                                        textAlign: 'center',
                                        color: 'white'
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            mb: 1
                                        }}
                                    >
                                        {selectedImage.title}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            opacity: 0.9
                                        }}
                                    >
                                        {selectedImage.description}
                                    </Typography>
                                </Box>

                                {/* Image Counter */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -40,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        color: 'white',
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {modalImageIndex + 1} / {images.length}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default ImageCarousel; 